import React, { useState, useEffect, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { socket } from "../utils/socket";
import { useDashboard } from "./dashboardStore";

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;

const AlertsAndTasksTable = () => {
	// Get current user from MSAL accounts
	const { accounts } = useMsal();
	const currentUser = accounts[0]?.name;

	const today = new Date();
	const formattedDate = today.toLocaleDateString("en-GB", {
		weekday: "long",
		day: "numeric",
		month: "long",
		year: "numeric",
	});

	// State variables
	const [users, setUsers] = useState([]);
	const [tasks, setTasks] = useState([]);
	const [items, setItems] = useState([]);
	const [search, setSearch] = useState("");
	const [showModal, setShowModal] = useState(false);
	const [errors, setErrors] = useState({});
	const [newTask, setNewTask] = useState({
		fromName: currentUser || "",
		toName: "",
		task: "",
		importance: "Medium",
	});

	const rowsPerPage = 6;
	const [myTasksPage, setMyTasksPage] = useState(1);
	const [allTasksPage, setAllTasksPage] = useState(1);

	// Socket updates: add and delete tasks
	useEffect(() => {
		if (!socket) return;

		const handleAlert = (data) => {
			if (data.type === "taskDeleted") {
				setTasks(prevTasks => prevTasks.filter(t => t.id !== Number(data.id)));
				setItems(prevItems => prevItems.filter(item => item.id !== Number(data.id)));
			}

			if (data.type === "taskAdded") {
				const savedTask = data.newTask;

				const senderUser = users.find(u => u.id === Number(savedTask.from));
				const recvUser = users.find(u => u.id === Number(savedTask.to));

				const senderName = senderUser?.userName || "Unknown";
				const recvName = recvUser?.userName || "Unknown";

				const taskForUI = {
					id: savedTask.id,
					fromName: senderName,
					toName: recvName.toLowerCase() === currentUser.toLowerCase() ? currentUser : recvName,
					task: savedTask.task,
					importance: savedTask.importance,
					date: savedTask.date,
				};

				setItems(prev => [taskForUI, ...prev]);
				setTasks(prev => [taskForUI, ...prev]);

				// Reset newTask form
				setNewTask({
					fromName: currentUser || users[0]?.userName || "",
					toName: users[0]?.userName || "",
					task: "",
					importance: "Medium",
				});
			}
		};

		socket.on("alert", handleAlert);

		return () => {
			socket.off("alert", handleAlert);
		};
	}, [currentUser, users]);

	// Subscribe to dashboard store slice
const usersDat = useDashboard(state => state.data?.users);

// -------------------------------------
// Process users data (with 24h localStorage cache)
// -------------------------------------
useEffect(() => {
	// Wait until store has data
	if (!usersDat) return;

	const CACHE_KEY = "usersCache";
	const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24h

	// Check localStorage first
	const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
	const now = Date.now();
	if (cached && now - cached.timestamp < CACHE_DURATION) {
		setUsers(cached.data || []);
		return; // use cache, skip processing
	}

	const processUsers = () => {
		try {
			// Normalize each user row

			// Save to state
			setUsers(usersDat);

			// Cache locally for 24h
			localStorage.setItem(
				CACHE_KEY,
				JSON.stringify({ data: usersDat, timestamp: Date.now() })
			);
		} catch (err) {
			console.error("Error processing users:", err);
		}
	};

	processUsers();
}, [usersDat]); // Reactive: runs whenever store updates


// Subscribe to dashboard store slice
const tasksDat = useDashboard(state => state.data?.tasks);

// -------------------------------------
// Process mistakes requests data
// -------------------------------------
useEffect(() => {
  // Wait until store has data
  if (!tasksDat) return;

  try {
    // Save to state
			setTasks(tasksDat);
			setItems(tasksDat);
  } catch (err) {
    console.error("Error processing tasks", err);
    setItems([]);
  }
}, [tasksDat]); // Reactive: runs whenever store updates


	// Set default "to" and "from" in newTask when users load
	useEffect(() => {
		if (users.length > 0) {
			setNewTask((prev) => ({
				...prev,
				toName: users[0].userName,
				fromName: currentUser || users[0].userName,
			}));
		}
	}, [users, currentUser]);

	// Filtered items based on search input
	const filteredItems = React.useMemo(() => {
		return items.filter(
			i =>
				(i.fromName || "").toLowerCase().includes(search.toLowerCase()) ||
				(i.toName || "").toLowerCase().includes(search.toLowerCase()) ||
				(i.task || "").toLowerCase().includes(search.toLowerCase()) ||
				(i.importance || "").toLowerCase().includes(search.toLowerCase())
		);
	}, [items, search]);

	// Only tasks assigned to current user
	const myTasks = React.useMemo(() => {
		return items.filter(i => i.toName === currentUser);
	}, [items, currentUser]);

	// Delete task
	const handleDelete = async (id) => {
		const confirmDelete = window.confirm(
			"⚠️ Are you sure you want to mark this task as completed?"
		);
		if (!confirmDelete) return;

		try {
			const res = await fetch(`${apiBaseUrl}/api/tasks/${id}`, { method: "DELETE" });
			if (!res.ok) throw new Error("Failed to delete task");
		} catch (err) {
			console.error("Failed to delete task:", err);
			alert("Failed to delete task");
		}
	};

	// Validate new task before posting
	const validateTask = () => {
		const newErrors = {};
		if (!newTask.fromName) newErrors.from = "From is required";
		if (!newTask.toName) newErrors.to = "To is required";
		if (!newTask.task.trim()) newErrors.task = "Task description is required";
		if (!newTask.importance) newErrors.importance = "Importance is required";
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	// Add a new task
	const handleAddTask = async () => {
		if (!validateTask()) return;

		const fromUser = users.find((u) => u.userName === newTask.fromName);
		const toUser = users.find((u) => u.userName === newTask.toName);
		if (!fromUser || !toUser) {
			alert("Invalid user selection");
			return;
		}

		const taskToPost = {
			fromName: fromUser.id,
			toName: toUser.id,
			task: newTask.task,
			importance: newTask.importance,
			date: new Date().toISOString().split("T")[0],
		};

		try {
			const res = await fetch(`${apiBaseUrl}/api/tasks`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(taskToPost),
			});
			if (!res.ok) throw new Error("Failed to add task");

			setShowModal(false);
			setErrors({});
		} catch (err) {
			console.error("Failed to add task:", err);
			alert("Failed to add task. Please try again.");
		}
	};

	// Pagination helpers
	const paginate = (array, page) =>
		array.slice((page - 1) * rowsPerPage, page * rowsPerPage);

	const totalMyTasksPages = Math.ceil(myTasks.length / rowsPerPage);
	const totalAllTasksPages = Math.ceil(filteredItems.length / rowsPerPage);



	return (
		<div className="w-full p-4 space-y-6">
			{/* Send Task Button */}
			<div className="flex justify-start mb-5">
				<button
					className="px-4 py-2 w-[200px] bg-blue-600 text-white rounded-md hover:bg-blue-700 transform cursor-pointer 
						hover:translate-x-2 duration-300 ease-in-out"
					onClick={() => setShowModal(true)}
				>
					Assign Task
				</button>
			</div>

			{/* My Tasks Table */}
			<h2 className="text-lg font-semibold mb-2 text-blue-600">My Tasks ({formattedDate}) </h2>

			{myTasks.length > 0 ? (
				<div className="overflow-x-auto rounded-md border border-blue-200 p-4">
					<table className="min-w-full divide-y divide-gray-100 text-sm md:text-base">
						<thead>
							<tr>
								<th className="px-3 py-2 text-center font-medium text-gray-700">#</th>
								<th className="px-3 py-2 text-center font-medium text-gray-700">From</th>
								<th className="px-3 py-2 text-center font-medium text-gray-700">To</th>
								<th className="px-3 py-2 text-center font-medium text-gray-700">Task</th>
								<th className="px-3 py-2 text-center font-medium text-gray-700">Date</th>
								<th className="px-3 py-2 text-center font-medium text-gray-700">Importance</th>
								<th className="px-3 py-2 text-center font-medium text-gray-700">Action</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-gray-100">
							{paginate(myTasks, myTasksPage).map((task, idx) => (
								<tr key={task.id} className="hover:bg-blue-100 even:bg-blue-50 odd:bg-white">
									<td className="px-3 py-2 text-center text-gray-600">
										{(myTasksPage - 1) * rowsPerPage + idx + 1}
									</td>
									<td className="px-3 py-2 text-center font-medium text-gray-800">{task.fromName}</td>
									<td className="px-3 py-2 text-center text-gray-600">{task.toName}</td>
									<td className="px-3 py-2 text-center text-gray-600">{task.task}</td>
									<td className="px-3 py-2 text-center text-gray-600">
										{new Date(task.date).toLocaleDateString("en-GB", { day: '2-digit', month: 'long', year: 'numeric' })}
									</td>
									<td className="px-3 py-2 text-center text-gray-600">{task.importance}</td>
									<td className="px-3 py-2 text-center">
										<button
											className="px-2 py-1 text-sm text-white bg-green-600 rounded-md hover:bg-green-700 cursor-pointer"
											onClick={() => handleDelete(task.id)}
										>
											Completed
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>

					{/* My Tasks Pagination */}
					{totalMyTasksPages > 1 && (
						<div className="flex justify-center gap-2 mt-2">
							<button
								disabled={myTasksPage === 1}
								className="px-2 py-1 border rounded disabled:opacity-50 cursor-pointer"
								onClick={() => setMyTasksPage((p) => p - 1)}
							>
								Prev
							</button>
							<span className="px-2 py-1">{myTasksPage} / {totalMyTasksPages}</span>
							<button
								disabled={myTasksPage === totalMyTasksPages}
								className="px-2 py-1 border rounded disabled:opacity-50 cursor-pointer"
								onClick={() => setMyTasksPage((p) => p + 1)}
							>
								Next
							</button>
						</div>
					)}
				</div>
			) : (
				<p className="text-gray-500 italic">No tasks assigned to you</p>
			)}

			{/* Search & All Tasks Table */}
			<h2 className="text-lg font-semibold mt-12 text-blue-600">All Tasks ({formattedDate})</h2>
			<div className="flex flex-col sm:flex-row items-center justify-between gap-2">
				<input
					type="text"
					placeholder="Search by from, to, task, or importance..."
					className="px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring focus:ring-blue-200 flex-1"
					value={search}
					onChange={(e) => setAllTasksPage(1) || setSearch(e.target.value)}
				/>
			</div>

			<div className="overflow-x-auto rounded-md border border-gray-200 mt-2">
				<table className="min-w-full divide-y divide-gray-100 text-sm md:text-base">
					<thead className="bg-gray-50">
						<tr>
							<th className="px-3 py-4 text-center font-medium text-gray-700">#</th>
							<th className="px-3 py-4 text-center font-medium text-gray-700">From</th>
							<th className="px-3 py-4 text-center font-medium text-gray-700">To</th>
							<th className="px-3 py-4 text-center font-medium text-gray-700">Task</th>
							<th className="px-3 py-4 text-center font-medium text-gray-700">Date</th>
							<th className="px-3 py-4 text-center font-medium text-gray-700">Importance</th>
							<th className="px-3 py-4 text-center font-medium text-gray-700">Action</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-gray-100">
						{paginate(filteredItems, allTasksPage).length === 0 ? (
							<tr>
								<td colSpan={7} className="px-3 py-4 text-center text-gray-500 italic">
									No tasks found
								</td>
							</tr>
						) : (
							paginate(filteredItems, allTasksPage).map((item, idx) => (
								<tr key={item.id} className="hover:bg-gray-300 even:bg-gray-200 odd:bg-white">
									<td className="px-3 py-2 text-center text-gray-600">
										{(allTasksPage - 1) * rowsPerPage + idx + 1}
									</td>
									<td className="px-3 py-2 text-center font-medium text-gray-800">{item.fromName}</td>
									<td className="px-3 py-2 text-center text-gray-600">{item.toName}</td>
									<td className="px-3 py-2 text-center text-gray-600">{item.task}</td>
									<td className="px-3 py-2 text-center text-gray-600">
										{new Date(item.date).toLocaleDateString("en-GB", { day: '2-digit', month: 'long', year: 'numeric' })}
									</td>
									<td className="px-3 py-2 text-center text-gray-600">{item.importance}</td>
									<td className="px-3 py-2 text-center">
										{item.toName === currentUser ? (
											<button
												className="px-2 py-1 text-sm text-white bg-green-600 rounded-md hover:bg-green-700 cursor-pointer"
												onClick={() => handleDelete(item.id)}
											>
												Completed
											</button>
										) : (
											<span className="text-gray-400 italic">Not your task</span>
										)}
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>

				{/* All Tasks Pagination */}
				{totalAllTasksPages > 1 && (
					<div className="flex justify-center gap-2 mt-2">
						<button
							disabled={allTasksPage === 1}
							className="px-2 py-1 border rounded disabled:opacity-50 cursor-pointer"
							onClick={() => setAllTasksPage((p) => p - 1)}
						>
							Prev
						</button>
						<span className="px-2 py-1">{allTasksPage} / {totalAllTasksPages}</span>
						<button
							disabled={allTasksPage === totalAllTasksPages}
							className="px-2 py-1 border rounded disabled:opacity-50 cursor-pointer"
							onClick={() => setAllTasksPage((p) => p + 1)}
						>
							Next
						</button>
					</div>
				)}
			</div>

			{/* Modal for adding new task */}
			{showModal && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
					<div className="bg-white w-full max-w-md rounded-md shadow-lg p-6 relative">
						<h2 className="text-lg font-semibold mb-4">Add New Task</h2>
						<div className="flex flex-col gap-3">
							<label className="font-bold text-left text-blue-600">From:</label>
							<select
								value={newTask.fromName}
								onChange={(e) => setNewTask({ ...newTask, fromName: e.target.value })}
								className="px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-200"
							>
								{users.map((u) => <option key={u.userName}>{u.userName}</option>)}
							</select>
							{errors.from && <p className="text-red-500 text-sm">{errors.from}</p>}

							<label className="font-bold text-left text-blue-600">To:</label>
							<select
								value={newTask.toName}
								onChange={(e) => setNewTask({ ...newTask, toName: e.target.value })}
								className="px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-200"
							>
								{users.map((u) => <option key={u.userName}>{u.userName}</option>)}
							</select>
							{errors.to && <p className="text-red-500 text-sm">{errors.to}</p>}

							<input
								type="text"
								value={newTask.task}
								onChange={(e) => setNewTask({ ...newTask, task: e.target.value })}
								placeholder="Task description"
								className="px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-200"
							/>
							{errors.task && <p className="text-red-500 text-sm">{errors.task}</p>}

							<select
								value={newTask.importance}
								onChange={(e) => setNewTask({ ...newTask, importance: e.target.value })}
								className="px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-200"
							>
								<option>High</option>
								<option>Medium</option>
								<option>Low</option>
							</select>
							{errors.importance && <p className="text-red-500 text-sm">{errors.importance}</p>}
						</div>

						<div className="flex justify-end gap-2 mt-4">
							<button
								className="px-4 py-2 rounded-md border hover:bg-gray-100 cursor-pointer"
								onClick={() => setShowModal(false)}
							>
								Cancel
							</button>
							<button
								className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
								onClick={handleAddTask}
							>
								Add
							</button>
						</div>

						<button
							className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 cursor-pointer"
							onClick={() => setShowModal(false)}
						>
							✕
						</button>
					</div>
				</div>
			)}
		</div>
	);
};

export default AlertsAndTasksTable;
